import { Products } from '@ori-ui/icons';
import { styled } from '@ori-ui/mui';
import type { ImageSizesHelperParams } from '@ori/image-sizes-helper';
import { generateSrcSet, getImageVariants } from '@ori/image-sizes-helper';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';

import { DESKTOP_BREAKPOINT } from '~/constants';

const ProductImageRoot = styled('img')<{ ownerState: { isSmall: boolean } }>(
  ({ ownerState, theme }) => ({
    width: 70,
    height: 70,

    [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
      width: 88,
      height: 88,
      objectFit: 'scale-down',

      ...(ownerState.isSmall && {
        width: 65,
        height: 65,
      }),
    },
  }),
);

const ProductImageNoImage = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.primary,
  display: 'flex',
  fontSize: theme.typography.h3.fontSize,
  justifyContent: 'center',

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    width: 65,
    height: 65,
  },
}));

interface ProductImageProps {
  image: string;
  name: string;
  size?: string;
}

export const ProductImage: VFC<ProductImageProps> = ({ image, name, size }) => {
  const { getTestId } = useTestId();

  if (!image) {
    return (
      <ProductImageNoImage data-testid={getTestId('no-image')}>
        <Products fontSize="inherit" />
      </ProductImageNoImage>
    );
  }
  const imagesVariants: ImageSizesHelperParams = {
    url: image,
    params: [
      {
        width: 65,
        height: 65,
        quality: 90,
      },
      {
        width: 70,
        height: 70,
        quality: 90,
      },
      {
        width: 88,
        height: 88,
        quality: 90,
      },
      {
        width: 105,
        height: 105,
        quality: 70,
      },
      {
        width: 123,
        height: 123,
        quality: 70,
      },
      {
        width: 140,
        height: 140,
        quality: 50,
      },
      {
        width: 184,
        height: 184,
        quality: 50,
      },
      {
        width: 193,
        height: 193,
        quality: 35,
      },
      {
        width: 210,
        height: 210,
        quality: 35,
      },
    ],
  };

  const images = getImageVariants(imagesVariants);
  const isSmall = size === 'small';
  const imageSizes = isSmall ? '65px' : '(max-width: 1023px) 70px, 88px';
  const ownerState = { isSmall };

  return (
    <ProductImageRoot
      data-testid={getTestId('product-image-root')}
      alt={name}
      ownerState={ownerState}
      srcSet={generateSrcSet(images)}
      sizes={imageSizes}
      src={images.at(-1)?.url}
    />
  );
};
